import React, { useState, useEffect } from 'react';
import { API_ROOT } from '../services/api';
import { generateInvoicePDF, getMonthName } from '../utils/invoicePdfGenerator';
import './BillingDashboard.css';

const API_URL = `${API_ROOT}/api`;

const BillingDashboard = ({ user }) => {
    const [stats, setStats] = useState(null);
    const [monthlyData, setMonthlyData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState('all'); // New state for month filter
    const [view, setView] = useState('summary'); // 'summary' | 'invoices'
    const [invoices, setInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [showRevokeModal, setShowRevokeModal] = useState(false);
    const [selectedInvoiceToRevoke, setSelectedInvoiceToRevoke] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const openRevokeModal = (invoice) => {
        setSelectedInvoiceToRevoke(invoice);
        setShowRevokeModal(true);
    };

    const openDetailsModal = async (invoice) => {
        try {
            setDetailsLoading(true);
            setShowDetailsModal(true);
            const token = localStorage.getItem('token');
            // Fetch details similar to PDF generation
            const res = await fetch(`${API_URL}/admin/billing/invoice-details?year=${invoice.year}&month=${invoice.month}&therapistId=${invoice.therapist_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();

            // Mark excluded sessions
            let exclusions = [];
            if (invoice.excluded_session_ids) {
                exclusions = typeof invoice.excluded_session_ids === 'string'
                    ? JSON.parse(invoice.excluded_session_ids)
                    : invoice.excluded_session_ids;
            }
            const excludedSet = new Set(exclusions);

            const processedSessions = (data.sessions || []).map(session => ({
                ...session,
                isExcluded: excludedSet.has(session.id)
            }));

            setSelectedInvoiceDetails({
                ...invoice,
                sessions: processedSessions,
                therapistData: data.therapistData
            });
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            alert('Error al cargar los detalles de la factura');
            setShowDetailsModal(false);
        } finally {
            setDetailsLoading(false);
        }
    };

    const closeDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedInvoiceDetails(null);
    };

    const closeRevokeModal = () => {
        setShowRevokeModal(false);
        setSelectedInvoiceToRevoke(null);
    };

    const confirmRevoke = async () => {
        if (!selectedInvoiceToRevoke) return;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/admin/billing/revoke-invoice`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ id: selectedInvoiceToRevoke.id })
            });
            const data = await res.json();
            if (data.success) {
                // alert('Factura devuelta correctamente'); // Optional: show toast instead
                fetchInvoices(); // Refresh list
                closeRevokeModal();
            } else {
                alert('Error al devolver factura: ' + data.message);
            }
        } catch (error) {
            console.error('Error revoking invoice:', error);
            alert('Error al conectar con el servidor');
        }
    };

    useEffect(() => {
        if (view === 'summary') {
            fetchAllData();
        } else if (view === 'invoices') {
            fetchInvoices();
        }
    }, [selectedYear, selectedMonth, view]);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const [statsRes, billingRes] = await Promise.all([
                fetch(`${API_URL}/admin/sync/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                fetch(`${API_URL}/admin/sync/monthly-billing?year=${selectedYear}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            const statsData = await statsRes.json();
            const billingData = await billingRes.json();

            setStats(statsData);
            setMonthlyData(billingData.data || []);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchInvoices = async () => {
        try {
            setInvoicesLoading(true);
            const token = localStorage.getItem('token');
            let url = `${API_URL}/admin/billing/invoice-submissions?year=${selectedYear}`;
            if (selectedMonth !== 'all') {
                url += `&month=${selectedMonth}`;
            }

            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setInvoices(data.submissions);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            setInvoicesLoading(false);
        }
    };

    const executeSync = async () => {
        try {
            setSyncing(true);
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/admin/sync/year`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });
            await fetchAllData();
        } catch (error) {
            console.error('Sync error:', error);
        } finally {
            setSyncing(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount || 0);
    };

    const getMonthName = (month) => {
        const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        // Backend stores months as 0-11 (JS Date format)
        return months[parseInt(month)] || '';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Aggregate monthly data
    const monthlyTotals = {};
    monthlyData.forEach(row => {
        const month = parseInt(row.month);
        if (!monthlyTotals[month]) {
            monthlyTotals[month] = { sessions: 0, revenue: 0, paid: 0 };
        }
        monthlyTotals[month].sessions += parseInt(row.billable_sessions) || 0;
        monthlyTotals[month].revenue += parseFloat(row.total_revenue) || 0;
        monthlyTotals[month].paid += parseFloat(row.paid_amount) || 0;
    });

    // Therapist totals
    const therapistTotals = {};
    monthlyData.forEach(row => {
        if (!therapistTotals[row.therapist_name]) {
            therapistTotals[row.therapist_name] = { sessions: 0, revenue: 0 };
        }
        therapistTotals[row.therapist_name].sessions += parseInt(row.billable_sessions) || 0;
        therapistTotals[row.therapist_name].revenue += parseFloat(row.total_revenue) || 0;
    });

    const totalSessions = Object.values(monthlyTotals).reduce((s, m) => s + m.sessions, 0);
    const totalRevenue = Object.values(monthlyTotals).reduce((s, m) => s + m.revenue, 0);
    const totalPaid = Object.values(monthlyTotals).reduce((s, m) => s + m.paid, 0);

    const handleDownloadPDF = async (invoice) => {
        try {
            const token = localStorage.getItem('token');
            const { year, month, therapist_id } = invoice;

            // Fetch Centre Data and Invoice Details (Therapist Data + Sessions)
            // We need a new endpoint to get full details for admin reconstruction
            const [centerRes, detailsRes] = await Promise.all([
                // Add timestamp to prevent caching
                fetch(`${API_URL}/admin/billing/center-data?t=${Date.now()}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                fetch(`${API_URL}/admin/billing/invoice-details?year=${year}&month=${month}&therapistId=${therapist_id}&t=${Date.now()}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            const centerData = await centerRes.json();
            const details = await detailsRes.json();
            const therapistData = details.therapistData || {};
            const sessions = details.sessions || [];

            // Filter excluded sessions (using the stored excluded_session_ids from the invoice record)
            // The details endpoint might return all sessions, so we filter again to be safe
            // or rely on what matches the totals. 
            // Better: Filter by the IDs in invoice.excluded_session_ids
            let exclusions = [];
            if (invoice.excluded_session_ids) {
                exclusions = typeof invoice.excluded_session_ids === 'string'
                    ? JSON.parse(invoice.excluded_session_ids)
                    : invoice.excluded_session_ids;
            }
            const excludedSet = new Set(exclusions);

            const activeSessions = sessions.filter(s => !excludedSet.has(s.id));

            // Generar PDF usando el motor unificado de Esencialmente Psicología
            generateInvoicePDF({
                snapshot: details.snapshot || invoice.invoice_snapshot,
                invoice,
                therapistData,
                centerData,
                sessions: activeSessions,
                month: invoice.month,
                year: invoice.year,
                invoiceNumber: invoice.invoice_number,
                therapistPercentage: 100 - parseFloat(invoice.center_percentage || 40),
                irpf: parseFloat(invoice.irpf_percentage || 0),
                vatTreatment: invoice.vat_treatment,
                submissionDate: invoice.submitted_at,
                saveAsFile: true
            });

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error al generar el PDF. Inténtelo de nuevo.');
        }
    };

    if (loading) {
        return <div className="bd-loading">Cargando dashboard...</div>;
    }

    return (
        <div className="bd-container">
            {/* Header */}
            <div className="bd-header">
                <div className="bd-title">
                    <h2>📊 Dashboard de Facturación</h2>
                </div>

                {/* View Tabs */}
                <div className="bd-tabs">
                    <button
                        className={`bd-tab ${view === 'summary' ? 'active' : ''}`}
                        onClick={() => setView('summary')}
                    >
                        Resumen
                    </button>
                    <button
                        className={`bd-tab ${view === 'invoices' ? 'active' : ''}`}
                        onClick={() => setView('invoices')}
                    >
                        Facturas Presentadas
                    </button>
                </div>

                <div className="bd-actions">
                    {view === 'invoices' && (
                        <select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="bd-select"
                            style={{ marginRight: '10px' }}
                        >
                            <option value="all">Todo el año</option>
                            {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map((m, i) => (
                                <option key={i} value={i}>{m}</option>
                            ))}
                        </select>
                    )}
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="bd-select"
                    >
                        {[2023, 2024, 2025, 2026].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                    {view === 'summary' && (
                        <button
                            className="bd-sync-btn"
                            onClick={executeSync}
                            disabled={syncing}
                        >
                            {syncing ? '⏳' : '🔄'} Sincronizar
                        </button>
                    )}
                </div>
            </div>

            {view === 'invoices' ? (
                <div className="bd-panel full-width">
                    <h3>📄 Historial de Facturas ({selectedYear})</h3>
                    {invoicesLoading ? (
                        <div className="bd-loading-section">Cargando facturas...</div>
                    ) : invoices.length === 0 ? (
                        <div className="bd-empty-state">No hay facturas presentadas para este año.</div>
                    ) : (
                        <div className="bd-table-responsive">
                            <table className="bd-table invoices-table">
                                <thead>
                                    <tr>
                                        <th>Fecha Presentación</th>
                                        <th>Mes Facturado</th>
                                        <th>Terapeuta</th>
                                        <th>Nº Factura</th>
                                        <th>Importe Total</th>
                                        <th>Info</th>
                                        <th>Estado</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map(inv => {
                                        const exclusions = inv.excluded_session_ids ? (typeof inv.excluded_session_ids === 'string' ? JSON.parse(inv.excluded_session_ids) : inv.excluded_session_ids) : [];
                                        const excludedCount = exclusions.length;

                                        return (
                                            <tr key={inv.id}>
                                                <td>{formatDate(inv.submitted_at)}</td>
                                                <td>{getMonthName(inv.month)} {inv.year}</td>
                                                <td>
                                                    <span
                                                        className="therapist-badge"
                                                        style={{ borderLeft: `4px solid ${inv.therapist_color || '#ccc'}`, paddingLeft: '8px' }}
                                                    >
                                                        {inv.therapist_name}
                                                    </span>
                                                </td>
                                                <td>{inv.invoice_number || '-'}</td>
                                                <td style={{ fontWeight: 'bold' }}>{formatCurrency(inv.total_amount)}</td>
                                                <td>
                                                    {excludedCount > 0 && (
                                                        <span className="exclusion-tag" title={`${excludedCount} sesiones excluidas`}>
                                                            ⚠️ {excludedCount} Excl.
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="status-badge success">Presentada</span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button
                                                            className="btn-icon"
                                                            onClick={() => openDetailsModal(inv)}
                                                            title="Ver Detalle de Sesiones"
                                                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.2rem' }}
                                                        >
                                                            👁️
                                                        </button>
                                                        <button
                                                            className="btn-icon"
                                                            onClick={() => handleDownloadPDF(inv)}
                                                            title="Descargar PDF"
                                                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.2rem' }}
                                                        >
                                                            📥
                                                        </button>
                                                        <button
                                                            className="btn-icon"
                                                            onClick={async () => {
                                                                if (window.confirm(`¿Estás seguro de que quieres devolver esta factura de ${inv.therapist_name}? Esto permitirá al terapeuta volver a generarla.`)) {
                                                                    try {
                                                                        const token = localStorage.getItem('token');
                                                                        const res = await fetch(`${API_URL}/admin/billing/revoke-invoice`, {
                                                                            method: 'POST',
                                                                            headers: {
                                                                                'Content-Type': 'application/json',
                                                                                Authorization: `Bearer ${token}`
                                                                            },
                                                                            body: JSON.stringify({ id: inv.id })
                                                                        });
                                                                        const data = await res.json();
                                                                        if (data.success) {
                                                                            alert('Factura devuelta correctamente');
                                                                            fetchInvoices(); // Refresh list
                                                                        } else {
                                                                            alert('Error al devolver factura: ' + data.message);
                                                                        }
                                                                    } catch (error) {
                                                                        console.error('Error revoking invoice:', error);
                                                                        alert('Error al conectar con el servidor');
                                                                    }
                                                                }
                                                            }}
                                                            title="Devolver Factura (Permitir corrección)"
                                                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1.2rem' }}
                                                        >
                                                            ↩️
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {/* Quick Stats Row */}
                    <div className="bd-quick-stats">
                        <div className="bd-stat">
                            <span className="bd-stat-value">{stats?.sessions?.total_sessions || 0}</span>
                            <span className="bd-stat-name">Sesiones</span>
                        </div>
                        <div className="bd-stat">
                            <span className="bd-stat-value">{stats?.sessions?.billable_sessions || 0}</span>
                            <span className="bd-stat-name">Facturables</span>
                        </div>
                        <div className="bd-stat">
                            <span className="bd-stat-value">{stats?.patients?.total || 0}</span>
                            <span className="bd-stat-name">Pacientes</span>
                        </div>
                        <div className="bd-stat">
                            <span className="bd-stat-value">{stats?.sessions?.therapists_with_sessions || 0}</span>
                            <span className="bd-stat-name">Terapeutas</span>
                        </div>
                    </div>

                    {/* Two Column Layout */}
                    <div className="bd-grid">
                        {/* Left: Monthly Table */}
                        <div className="bd-panel">
                            <h3>📅 Resumen Mensual {selectedYear}</h3>
                            <table className="bd-table">
                                <thead>
                                    <tr>
                                        <th>Mes</th>
                                        <th>Sesiones</th>
                                        <th>Facturado</th>
                                        <th>Cobrado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => {
                                        const data = monthlyTotals[month] || { sessions: 0, revenue: 0, paid: 0 };
                                        if (data.sessions === 0) return null;
                                        return (
                                            <tr key={month}>
                                                <td><strong>{getMonthName(month)}</strong></td>
                                                <td>{data.sessions}</td>
                                                <td>{formatCurrency(data.revenue)}</td>
                                                <td className="green">{formatCurrency(data.paid)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td><strong>TOTAL</strong></td>
                                        <td><strong>{totalSessions}</strong></td>
                                        <td><strong>{formatCurrency(totalRevenue)}</strong></td>
                                        <td className="green"><strong>{formatCurrency(totalPaid)}</strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* Right: Therapists */}
                        <div className="bd-panel">
                            <h3>👨‍⚕️ Por Terapeuta</h3>
                            <div className="bd-therapist-list">
                                {Object.entries(therapistTotals)
                                    .sort((a, b) => b[1].revenue - a[1].revenue)
                                    .map(([name, data]) => (
                                        <div key={name} className="bd-therapist-row">
                                            <span className="bd-t-name">{name}</span>
                                            <span className="bd-t-sessions">{data.sessions} ses.</span>
                                            <span className="bd-t-revenue">{formatCurrency(data.revenue)}</span>
                                        </div>
                                    ))
                                }
                                {Object.keys(therapistTotals).length === 0 && (
                                    <p className="bd-empty">Sin datos para este año</p>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Custom Revoke Modal */}
            {showRevokeModal && selectedInvoiceToRevoke && (
                <div className="bd-modal-overlay">
                    <div className="bd-modal">
                        {/* ... existing revoke modal content ... */}
                        <div className="bd-modal-header">
                            <h3 className="bd-modal-title">Devolver Factura</h3>
                        </div>
                        <div className="bd-modal-body">
                            <p>
                                ¿Estás seguro de que quieres devolver la factura de <strong>{selectedInvoiceToRevoke.therapist_name}</strong>?
                            </p>
                            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '10px' }}>
                                Esto eliminará la factura actual y permitirá al terapeuta volver a generarla para corregir errores.
                            </p>
                        </div>
                        <div className="bd-modal-footer">
                            <button className="bd-btn bd-btn-secondary" onClick={closeRevokeModal}>
                                Cancelar
                            </button>
                            <button className="bd-btn bd-btn-danger" onClick={confirmRevoke}>
                                Devolver Factura
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedInvoiceDetails && (
                <div className="bd-modal-overlay">
                    <div className="bd-modal bd-modal-lg">
                        <div className="bd-modal-header">
                            <h3 className="bd-modal-title">Detalle de Factura</h3>
                            <button onClick={closeDetailsModal} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                        </div>

                        {detailsLoading ? (
                            <div className="bd-loading">Cargando detalles...</div>
                        ) : (
                            <>
                                <div className="bd-details-info">
                                    <strong>Terapeuta:</strong> {selectedInvoiceDetails.therapistData?.full_name} <br />
                                    <strong>Periodo:</strong> {getMonthName(selectedInvoiceDetails.month)} {selectedInvoiceDetails.year}
                                </div>

                                <div className="bd-session-list">
                                    {selectedInvoiceDetails.sessions && selectedInvoiceDetails.sessions.length > 0 ? (
                                        selectedInvoiceDetails.sessions.map(session => (
                                            <div key={session.id} className={`bd-session-item ${session.isExcluded ? 'excluded' : ''}`}>
                                                <div className="bd-session-main">
                                                    <div className="bd-session-date">
                                                        {new Date(session.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                    <div className="bd-session-patient">
                                                        {session.patient_name}
                                                    </div>
                                                </div>
                                                <div className="bd-session-meta">
                                                    <div className="bd-session-price">
                                                        {formatCurrency(session.modified_price || session.price || 0)}
                                                    </div>
                                                    {session.isExcluded && (
                                                        <span className="bd-excluded-badge">Excluida</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                                            No se encontraron sesiones.
                                        </div>
                                    )}
                                </div>
                                <div className="bd-modal-footer" style={{ marginTop: '20px' }}>
                                    <button className="bd-btn bd-btn-secondary" onClick={closeDetailsModal}>
                                        Cerrar
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingDashboard;
